/**
 * @fileoverview Live local-first state and rerun actions for generic department repair jobs.
 * @module system-web/hooks/useDepartmentRepairJobs
 *
 * Responsibilities:
 * - Subscribe to tenant and repair-job Dexie reads through a pure liveQuery.
 * - Derive per-tenant repair status, including expired leases.
 * - Expose rerun submission, error, and already-active notice state.
 *
 * Dependencies:
 * - dexie liveQuery.
 * - ../services/departmentRepairJobService and delivery tenant listing.
 *
 * Architecture:
 * - The hook observes only pure Dexie reads; online requests are invoked by rerun.
 *
 * Notes:
 * - Lookup-specific UI uses this hook with its job type and route; other repairs can reuse it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { liveQuery } from 'dexie';
import { listRepairTenants, type RepairTenantRow } from '../services/deliveryDuplicateRepairService';
import {
  listDepartmentRepairJobs,
  requestDepartmentRepair,
  type DepartmentRepairJob,
} from '../services/departmentRepairJobService';

/** @contract Status values shown by the shared department repair table. */
export type DepartmentRepairStatus = 'failed' | 'repaired' | 'upToDate' | 'skipped' | 'running' | 'notRun';

/**
 * @contract DepartmentRepairRow
 * Purpose: One department's latest repair status and optional job.
 * Fields: Dexie tenant, derived status, and newest matching repair job.
 * Rules: An active row with an expired lease is displayed as failed/interrupted.
 * Sync: Derived from pull-synced `tenants` and `background_jobs`.
 */
export interface DepartmentRepairRow {
  tenant: RepairTenantRow;
  status: DepartmentRepairStatus;
  job?: DepartmentRepairJob;
}

interface DepartmentRepairState {
  tenants: RepairTenantRow[];
  jobs: DepartmentRepairJob[];
}

function isExpiredActiveJob(job: DepartmentRepairJob): boolean {
  if (job.status !== 'pending' && job.status !== 'running') return false;
  const leaseExpiresAt = job.payload?.leaseExpiresAt;
  return typeof leaseExpiresAt === 'string'
    && Number.isFinite(Date.parse(leaseExpiresAt))
    && Date.parse(leaseExpiresAt) < Date.now();
}

function deriveStatus(job?: DepartmentRepairJob): DepartmentRepairStatus {
  if (!job) return 'notRun';
  if (isExpiredActiveJob(job)) return 'failed';
  if (job.status === 'pending' || job.status === 'running') return 'running';
  if (job.status === 'failed' || job.payload?.outcome === 'failed') return 'failed';
  if (job.payload?.outcome === 'skipped') return 'skipped';
  if (job.status === 'completed') return Number(job.payload?.stampedRows ?? 0) === 0 ? 'upToDate' : 'repaired';
  return 'notRun';
}

/**
 * @hook useDepartmentRepairJobs
 * Purpose: Drive a repair table from synchronized local tenant/job state.
 * Returns: Rows, loading/submitting/error/notice state, and rerun(tenantIds).
 * Side Effects: Subscribes to Dexie liveQuery and sends chunked API requests only on rerun.
 * Offline Behavior: Last synced rows remain visible; reruns surface connectivity errors.
 */
export function useDepartmentRepairJobs(jobType: string, path: string) {
  const [tenants, setTenants] = useState<RepairTenantRow[]>([]);
  const [jobs, setJobs] = useState<DepartmentRepairJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const readState = useCallback(async (): Promise<DepartmentRepairState> => {
    const [tenantRows, jobRows] = await Promise.all([
      listRepairTenants(),
      listDepartmentRepairJobs(jobType),
    ]);
    return { tenants: tenantRows, jobs: jobRows };
  }, [jobType]);

  useEffect(() => {
    const subscription = liveQuery(readState).subscribe({
      next: (state) => {
        setTenants(state.tenants);
        setJobs(state.jobs);
        setIsLoading(false);
      },
      error: (cause) => {
        setError(cause instanceof Error ? cause.message : 'Failed to load department repair jobs');
        setIsLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [readState]);

  const rows = useMemo<DepartmentRepairRow[]>(() => {
    const latestByTenant = new Map<string, DepartmentRepairJob>();
    for (const job of jobs) {
      const tenantId = String(job.tenantId ?? '').toLowerCase();
      if (!latestByTenant.has(tenantId)) latestByTenant.set(tenantId, job);
    }
    return tenants.map((tenant) => {
      const job = latestByTenant.get(tenant._id.toLowerCase());
      const expired = job ? isExpiredActiveJob(job) : false;
      const displayedJob: DepartmentRepairJob | undefined = expired && job
        ? { ...job, status: 'failed', message: 'Interrupted; re-run' }
        : job;
      return { tenant, status: deriveStatus(job), job: displayedJob };
    });
  }, [tenants, jobs]);

  const rerun = useCallback(async (tenantIds: string[]): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await requestDepartmentRepair(path, tenantIds);
      if (result.alreadyActive.length > 0) {
        setNotice(`${result.alreadyActive.length} department(s) already have an active repair.`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to start department repair');
      throw cause;
    } finally {
      setIsSubmitting(false);
    }
  }, [path]);

  return { rows, isLoading, isSubmitting, error, notice, rerun };
}
